import { useMemo, useRef, useState } from "react";
import { openingOriginOf } from "../useGapsSheet";

/**
 * What a sheet hosted off the shared intent draws, and which OPENING that is.
 *
 * `shown` is the target while the sheet is up and the last one after it closes, so the drawer animates out with its
 * content rather than empty. `openingKey` changes exactly when a new target object arrives — the store's intent object
 * is stable between writes, so a new object IS a new opening — which lets the host key its content by it: each
 * opening mounts a fresh form (fresh prefill, fresh step), and a re-render of the same opening keeps what the user
 * typed.
 */
export const useSheetOpening = <T extends object>(target: T | null): { shown: T | null; openingKey: number | null } => {
	const [retained, setRetained] = useState<T | null>(target);
	// Adjusted during render — React's pattern for state that follows a prop — so the first frame is already right.
	if (target !== null && target !== retained) setRetained(target);
	// DEFAULT: the last target once the sheet closes — only for its exit animation, never to act on (see above).
	const shown = target ?? retained;

	const registry = useRef<{ ids: WeakMap<T, number>; count: number }>({ ids: new WeakMap(), count: 0 });
	const openingKey = useMemo(() => {
		if (shown === null) return null;
		// Keyed by the intent the opening began as: a page that moved to another day is still the same opening.
		const origin = openingOriginOf(shown);
		const known = registry.current.ids.get(origin);
		if (known !== undefined) return known;
		registry.current.count += 1;
		registry.current.ids.set(origin, registry.current.count);
		return registry.current.count;
	}, [shown]);

	return { shown, openingKey };
};
